<template>
  <slot name="default" :countdown="countdown" :remaining-ms="remainingMs" />
</template>
<script setup lang="ts">
import { DateTime } from "ts-luxon";
import { computed, onBeforeMount, onUnmounted, ref, watch } from "vue";
import {
  countdownTickMs,
  formatCountdown,
  zeroCountdown,
  type FormattedCountdown,
} from "@/util/countdown";

const props = defineProps<{
  to: DateTime;
}>();

const emit = defineEmits<{
  (e: "ended"): void;
}>();

/**
 * How long the display stays on "0 seconds" after the target passes before it
 * flips to "Ended". Parents reload on @ended and often hand us a new target
 * (the next dutch price step), so this avoids flashing "Ended" for a countdown
 * that is not actually over.
 */
const ENDED_GRACE_MS = 3000;

/** Wake a hair after the boundary rather than a hair before it. */
const TICK_SKEW_MS = 25;

const remainingMs = ref(0);

let timer: ReturnType<typeof setTimeout> | null = null;
let endFired = false;

const countdown = computed<FormattedCountdown>(() => {
  if (remainingMs.value > 0) {
    return formatCountdown(remainingMs.value);
  }

  if (remainingMs.value > -ENDED_GRACE_MS) {
    return zeroCountdown();
  }

  return formatCountdown(0);
});

function tick() {
  timer = null;
  const ms = props.to.diff(DateTime.now()).toMillis();

  // An invalid DateTime diffs to NaN; treat it as already over rather than
  // scheduling a NaN delay, which the browser runs as an immediate loop.
  remainingMs.value = Number.isFinite(ms) ? ms : -ENDED_GRACE_MS;

  if (remainingMs.value <= 0 && !endFired) {
    endFired = true;
    emit("ended");
  }

  if (remainingMs.value <= -ENDED_GRACE_MS) {
    // Nothing left to count down to.
    return;
  }

  const delay =
    remainingMs.value > 0
      ? countdownTickMs(remainingMs.value) + TICK_SKEW_MS
      : ENDED_GRACE_MS + remainingMs.value + TICK_SKEW_MS;

  timer = setTimeout(tick, delay);
}

function restart() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  endFired = false;
  tick();
}

onBeforeMount(restart);

watch(() => props.to.toMillis(), restart);

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
});
</script>
